import { AvatarNew, Button } from '@platform/components';
import { DirectoryInnerSection } from '../ContentSection';
import { DirectoryLayout } from '../DirectoryLayout';
import { ROUTES } from '../../routes/consts';
import { SubheaderPanel } from '../SubheaderPanel';
import { PillsList } from '../PillsList';
import { TwoColumnInnerContent } from '../TwoColumnInnerContent';
import { VolunteerRolePanel } from './VolunteerRolePanel';
import { DirectoryOuterSection } from '../DirectoryContentGrid';
import { DescriptionText } from '../DescriptionText';
import { OrgPillsPanel } from '../organizations/OrgPillsPanel';
import { useParams } from 'wouter';
import { DirectoryProjects } from '@platform/client';
import { OrgInfoCard } from '../OrgInfoCard';
import { OrgDescriptions } from '../OrgDescriptions';
import { RepsList } from '../RepsList';
import MetaTags from '../../util/MetaTags';
import { useQueryClient } from '@tanstack/react-query';
import IntroductionStatusText, {
  isIntroStatusTextVisible,
  StatusText,
} from '../IntroductionStatusText';
import IntroductionCTA from '../IntroductionCTA';
import { DirectoryPageContext } from '../DirectoryPageContext';
import Skeleton from 'react-loading-skeleton';
import { JoinAlert } from '../JoinAlert';
import cx from 'classix';
import { DescriptionBulletList } from '../DescriptionBulletList';

const ProjectProfile = () => {
  const queryClient = useQueryClient();
  const { projectId } = useParams<{ projectId: string }>();
  const {
    isSuccess: isSuccess,
    data: project,
    queryKey,
  } = DirectoryProjects.useGetProjectById(parseInt(projectId));
  const leadRep = project?.orgReps.find((rep) => rep.id === project?.leadRepId);
  const hasDeliverables = project?.deliverables && project.deliverables.length > 0;

  const setSaveProject = DirectoryProjects.useSetSaveProject({
    mutation: {
      onSuccess: () => {
        // This is kind of wasteful, but the alternative is to manuall update the cache
        queryClient.invalidateQueries({
          queryKey: [...queryKey],
        });
      },
    },
  });

  if (!isSuccess) {
    return (
      <DirectoryLayout
        backLink={{ label: 'Browse projects', href: '~/platform' + ROUTES.SEARCH_PROJECTS }}
        banner={
          <div className='px-4 sm:px-8 bg-secondary-brand-25'>
            <div className='mx-auto sm:max-w-[1216px] flex flex-col sm:flex-row sm:px-4 py-8 gap-[18px] sm:gap-0'>
              <div className='flex flex-col gap-6 sm:gap-10 items-start'>
                <div>
                  <Skeleton width={600} />
                  <Skeleton width={300} />
                </div>
              </div>
              <div className='sm:ml-auto flex flex-col-reverse sm:flex-col items-center  justify-center sm:items-end gap-[18px] sm:gap-0'></div>
            </div>
          </div>
        }
      >
        <DirectoryOuterSection noTopMargin>
          <DirectoryInnerSection title='About'>
            <TwoColumnInnerContent>
              <div className='flex flex-col gap-6'>
                <SubheaderPanel title='Background'>
                  <Skeleton width={600} />
                  <Skeleton width={600} />
                  <Skeleton width={600} />
                </SubheaderPanel>
              </div>
            </TwoColumnInnerContent>
          </DirectoryInnerSection>
        </DirectoryOuterSection>
      </DirectoryLayout>
    );
  }

  const toggleProjectSaved = () => {
    setSaveProject.mutate({ projectId: parseInt(projectId), setSaved: !project.isSaved });
  };

  return (
    <DirectoryPageContext.Provider
      value={{
        targetUserId: project.leadRepId,
        sendingFromRole: 'PROFESSIONAL',
        queryKey: DirectoryProjects.getGetProjectByIdQueryKey(parseInt(projectId)),
      }}
    >
      <DirectoryLayout
        backLink={{ label: 'Browse projects', href: '~/platform' + ROUTES.SEARCH_PROJECTS }}
        banner={
          <div className='px-4 sm:px-8 bg-secondary-brand-25'>
            <div
              className={cx(
                'mx-auto sm:max-w-[1216px] sm:min-h-[116px] sm:items-end sm:px-4 py-8 gap-[18px] sm:gap-y-10',
                'flex flex-col',
                project.repMatchedDate ?
                  'flex-row-reverse items-center'
                : ' sm:grid sm:grid-cols-[auto_1fr]',
              )}
            >
              {/* A minimal copy of the IntroductionStatusText rendering for the edge case where a rep has
               * matched on this project, since they cannot see the actual intro CTA as would be required to
               * view IntroductionStatusText */}
              <div
                className={cx(
                  'sm:ml-auto sm:col-span-2 sm:text-nowrap',
                  !project?.repMatchedDate?.length &&
                    !isIntroStatusTextVisible(project.introCtaState.state) &&
                    'hidden',
                )}
              >
                <>
                  {project.repMatchedDate && <StatusText>{project.repMatchedDate}</StatusText>}
                  {isIntroStatusTextVisible(project.introCtaState.state) && (
                    <IntroductionStatusText introCtaState={project.introCtaState} />
                  )}
                </>
              </div>
              <div className='flex flex-col gap-6 sm:gap-10 items-start'>
                <div className='text-4xl font-semibold text-gray-900'>{project.name}</div>
              </div>
              <div className='sm:ml-auto flex flex-col-reverse sm:flex-col items-center justify-center sm:items-end gap-[18px] sm:gap-0'>
                <div className='flex flex-col w-full sm:w-auto sm:flex-row mt-auto gap-4 sm:gap-3 text-nowrap'>
                  {project?.userCanSave && (
                    <Button
                      variant='secondary-gray'
                      leadIcon='heart'
                      size='xl'
                      onClick={toggleProjectSaved}
                      isDisabled={setSaveProject.isPending}
                      leadIconClassNames={project.isSaved ? 'text-error-500' : undefined}
                    >
                      {project.isSaved ? 'Unsave' : 'Save'}
                    </Button>
                  )}
                  <IntroductionCTA
                    apiCtaState={project.introCtaState}
                    recipientName={leadRep?.name ?? project.orgDetails.orgName}
                    presetProjectId={parseInt(projectId)}
                    buttonProps={{ size: 'xl' }}
                    otherPartyType='organization'
                  />
                </div>
              </div>
            </div>
          </div>
        }
      >
        <MetaTags
          title={`${project.name} | MovingWorlds.org`}
          description={`${project.name} | MovingWorlds.org`}
        />
        <DirectoryOuterSection noTopMargin>
          <DirectoryInnerSection title='About'>
            <TwoColumnInnerContent>
              <div className='flex flex-col gap-6'>
                {project.backgroundDescription.length > 0 && (
                  <SubheaderPanel title='Background'>
                    <DescriptionText html={project.backgroundDescription} />
                  </SubheaderPanel>
                )}
                {hasDeliverables && (
                  <SubheaderPanel title='What you might do'>
                    <DescriptionBulletList items={project.deliverables} />
                  </SubheaderPanel>
                )}
                {project.whatYouMightDoDescription.length > 0 && !hasDeliverables && (
                  <SubheaderPanel title='What you might do'>
                    <DescriptionText html={project.whatYouMightDoDescription} />
                  </SubheaderPanel>
                )}
                {project.whatYouWillLearnDescription &&
                  project.whatYouWillLearnDescription.length > 0 && (
                    <SubheaderPanel title="What you'll learn">
                      <DescriptionText html={project.whatYouWillLearnDescription} />
                    </SubheaderPanel>
                  )}
                <SubheaderPanel title='Preferred skills'>
                  <PillsList items={project.expertises} />
                </SubheaderPanel>
              </div>
              <VolunteerRolePanel roleType={project.projectRoleType} />
            </TwoColumnInnerContent>
            {project.userCanViewJoinAlert && <JoinAlert />}
          </DirectoryInnerSection>
          <DirectoryInnerSection title='Organization'>
            <TwoColumnInnerContent>
              <div className='flex flex-col gap-5'>
                <div className='flex flex-col sm:flex-row items-center gap-2 sm:gap-0 text-center sm:text-left'>
                  <div className='w-14 h-14 sm:mr-3'>
                    <AvatarNew
                      url={project.orgDetails.avatarUrl}
                      withPadding
                      fallbackIcon='building-04'
                      alt='Organization Logo'
                    />
                  </div>
                  <div className='flex flex-col'>
                    <span className='text-base font-semibold text-gray-900'>
                      {project.orgDetails.orgName}
                    </span>
                    <span className='text-sm text-gray-500'>
                      {project.orgDetails.locationCity}, {project.orgDetails.locationCountry}
                    </span>
                  </div>
                  <div className='hidden sm:block ml-auto'>
                    <Button
                      variant='secondary-gray'
                      size='md'
                      tailIcon='arrow-narrow-up-right'
                      href={`/organizations/${project.orgId}`}
                    >
                      See full profile
                    </Button>
                  </div>
                </div>
                <OrgDescriptions orgData={project.orgDetails} maxLength={550} />
              </div>
              <OrgPillsPanel
                sustainabilityGoals={project.orgDetails.sustainabilityGoals}
                industries={project.orgDetails.industries}
              />
            </TwoColumnInnerContent>
            <OrgInfoCard isLoaded={isSuccess} orgData={project.orgDetails} />
            <div className='sm:hidden w-full'>
              <Button
                variant='secondary-gray'
                size='lg'
                tailIcon='arrow-narrow-up-right'
                href={`/organizations/${project.orgId}`}
              >
                See full profile
              </Button>
            </div>
          </DirectoryInnerSection>
          <DirectoryInnerSection title='Representatives'>
            <RepsList reps={project.orgReps} />
          </DirectoryInnerSection>
        </DirectoryOuterSection>
      </DirectoryLayout>
    </DirectoryPageContext.Provider>
  );
};

export default ProjectProfile;
